import { addDoc, arrayUnion, collection, doc, getDoc, getDocs, increment, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { CreateLeagueInput, LeagueRepository } from "@/modules/leagues/repositories/LeagueRepository";
import type { LeagueDetail, LeagueRole, LeagueSummary } from "@/modules/leagues/types/league";
import { getCurrentSeason } from "@/modules/leagues/types/league";

/**
 * Implementação REAL de LeagueRepository — Firestore (Parte 2 do escopo de
 * integração real). Substitui MockLeagueRepository como fonte de dados da
 * experiência principal (Home, criação de liga).
 *
 * IMPORTANTE sobre `myRole`: é uma propriedade por-usuário, não um campo
 * fixo da liga (diferente do Mock, onde só existia "sua" perspectiva).
 * Por isso o documento da liga guarda `memberUids` (array, pra permitir a
 * query "minhas ligas" via array-contains) e `memberRoles` (mapa uid→papel,
 * denormalizado só pra essa leitura barata). O registro completo e
 * definitivo de cada participante continua em `groups/{id}/participants/{uid}`
 * (já previsto na arquitetura) — isso aqui é só índice de consulta.
 *
 * PRÉ-REQUISITO: as Firestore Rules precisam liberar a coleção `groups`
 * (ver firestore.rules) — sem isso toda operação aqui falha com
 * permission-denied, mesmo com este código correto.
 */
export class FirestoreLeagueRepository implements LeagueRepository {
  private groupsCollection() {
    return collection(db, "groups");
  }

  private requireUid(): string {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Você precisa estar autenticado para essa ação.");
    return uid;
  }

  private toDomain(id: string, data: Record<string, unknown>, currentUid: string): LeagueDetail {
    const memberRoles = (data.memberRoles as Record<string, LeagueRole> | undefined) ?? {};
    const { memberUids: _memberUids, memberRoles: _memberRoles, ...rest } = data;
    return {
      ...(rest as Omit<LeagueDetail, "id" | "myRole">),
      id,
      myRole: memberRoles[currentUid] ?? "member",
    };
  }

  /**
   * `groups/{id}/participants/{uid}` é o registro DEFINITIVO de papel de
   * cada pessoa (ver comentário de classe) — `memberRoles` no doc da liga
   * é só um índice denormalizado pra consulta barata. Se os dois
   * divergirem por qualquer motivo (ex: liga criada fora do fluxo padrão,
   * migração, edição manual), o participante é quem manda: sem isso, o
   * botão de Configurações podia sumir pro admin de verdade mesmo com o
   * doc de participante dizendo "admin" corretamente.
   */
  private async resolveMyRole(groupId: string, uid: string, fallback: LeagueRole): Promise<LeagueRole> {
    if (!uid) return fallback;
    try {
      const participantSnap = await getDoc(doc(this.groupsCollection(), groupId, "participants", uid));
      const role = participantSnap.exists() ? (participantSnap.data().role as LeagueRole | undefined) : undefined;
      return role ?? fallback;
    } catch {
      return fallback;
    }
  }

  async getMyLeagues(): Promise<LeagueSummary[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];

    const q = query(this.groupsCollection(), where("memberUids", "array-contains", uid));
    const snap = await getDocs(q);
    const leagues = snap.docs.map((d) => this.toDomain(d.id, d.data(), uid));

    // Ordena pela liga acessada mais recentemente primeiro (item pedido:
    // "a última que ele entrou fica no topo") — NUNCA pela ordem de
    // criação. `lastAccessedAt` vive no documento de participante de cada
    // liga (por-usuário, já existia essa subcoleção — não duplica dado
    // novo), gravado por `touchLastAccessed` toda vez que o usuário entra
    // numa liga (ver LeagueShell). Liga nunca acessada (recém-criada,
    // ainda sem esse campo) cai pro fim — busca falhando também não
    // quebra a lista, só perde a ordenação (nunca esconde a liga).
    const lastAccessedEntries = await Promise.all(
      leagues.map(async (l) => {
        try {
          const participantSnap = await getDoc(doc(this.groupsCollection(), l.id, "participants", uid));
          const ts = participantSnap.exists() ? (participantSnap.data().lastAccessedAt as { toMillis?: () => number } | undefined) : undefined;
          return [l.id, ts?.toMillis?.() ?? 0] as const;
        } catch {
          return [l.id, 0] as const;
        }
      })
    );
    const lastAccessedByLeagueId = new Map(lastAccessedEntries);
    return leagues.sort((a, b) => (lastAccessedByLeagueId.get(b.id) ?? 0) - (lastAccessedByLeagueId.get(a.id) ?? 0));
  }

  async getLeagueById(leagueId: string): Promise<LeagueDetail | null> {
    const uid = auth.currentUser?.uid ?? "";
    const snap = await getDoc(doc(this.groupsCollection(), leagueId));
    if (!snap.exists()) return null;
    const league = this.toDomain(snap.id, snap.data(), uid);
    league.myRole = await this.resolveMyRole(leagueId, uid, league.myRole);
    return league;
  }

  async createLeague(input: CreateLeagueInput): Promise<LeagueDetail> {
    const uid = this.requireUid();
    const nome = auth.currentUser?.displayName?.trim() || "Você";
    const nowIso = new Date().toISOString();
    const inviteCode = Math.random().toString(36).slice(2, 8).toUpperCase();

    const groupData = {
      nome: input.nome,
      competitionId: input.competitionId,
      competitionCode: input.competitionId,
      competitionName: input.competitionName,
      season: getCurrentSeason(),
      bannerUrl: null,
      participantsCount: 1,
      status: "aberta" as const,
      currentRoundLabel: "Rodada 1",
      pendingPredictionsCount: 0,
      hasLiveMatch: false,
      inviteCode,
      createdAt: nowIso,
      descricao: input.descricao,
      maxParticipantes: input.maxParticipantes,
      privada: input.privada,
      permiteDesafios: input.permiteDesafios,
      permitePerguntasBonus: input.permitePerguntasBonus,
      permiteImportacaoManual: input.permiteImportacaoManual,
      permiteEntradaAposInicio: input.permiteEntradaAposInicio,
      disabledClubIds: [] as string[],
      // CONGELAMENTO imediato — a liga só existe depois que as regras já
      // estão congeladas (Addendum "Liga como Hub Central" §2.1).
      rules: { frozenAt: nowIso, rules: input.rules },
      memberUids: [uid],
      memberRoles: { [uid]: "admin" as LeagueRole },
    };

    const groupRef = await addDoc(this.groupsCollection(), groupData);

    // Mapeamento código -> ID (coleção `inviteCodes`) — é isso que permite
    // alguém que ainda não é membro localizar a liga pelo código, sem
    // precisar de permissão de leitura em `groups` (ver Firestore Rules).
    // Escrito só depois que a liga já existe, porque a Rule confere que o
    // código bate com o que está gravado nela.
    await setDoc(doc(db, "inviteCodes", inviteCode), { groupId: groupRef.id });

    // Registro completo do participante (fonte de verdade — ver arquitetura seção 9).
    await setDoc(doc(this.groupsCollection(), groupRef.id, "participants", uid), {
      uid,
      nome,
      avatarUrl: auth.currentUser?.photoURL ?? null,
      role: "admin" as LeagueRole,
      pontos: 0,
      entrouEm: nowIso,
      status: "ativo" as const,
    });

    // Primeira entrada do Histórico (append-only — ver arquitetura seção 10).
    await addDoc(collection(this.groupsCollection(), groupRef.id, "history"), {
      type: "rule_frozen",
      descricao: "Regras de pontuação congeladas na criação da liga.",
      administrador: nome,
      createdAt: serverTimestamp(),
    });

    const { memberUids: _u, memberRoles: _r, ...rest } = groupData;
    return { ...rest, id: groupRef.id, myRole: "admin" };
  }

  async joinLeagueByCode(inviteCode: string): Promise<LeagueDetail> {
    const uid = this.requireUid();
    const nome = auth.currentUser?.displayName?.trim() || "Você";
    const code = inviteCode.trim().toUpperCase();
    if (!code) throw new Error("Digite o código da liga.");

    // Resolve código -> ID da liga via `inviteCodes` (get direto por ID,
    // nunca uma busca/list na coleção `groups`) — é assim que um usuário
    // que AINDA NÃO é membro consegue localizar a liga sem precisar de
    // permissão de leitura nela (essa leitura continua tão protegida
    // quanto sempre foi). Ver Firestore Rules pro porquê disso ser seguro.
    const mappingSnap = await getDoc(doc(db, "inviteCodes", code));
    if (!mappingSnap.exists()) {
      throw new Error("Esse código de liga não foi encontrado.");
    }
    const groupId = mappingSnap.data().groupId as string;
    const groupRef = doc(this.groupsCollection(), groupId);

    // Não dá mais pra LER a liga antes de entrar (de propósito — é
    // exatamente isso que preserva a proteção original). "Já é membro" e
    // "liga cheia" agora são validados dentro da própria Firestore Rule de
    // update (`maxParticipantes` incluído nela); se a escrita for negada,
    // é por um desses dois motivos — o Firestore não diferencia qual.
    try {
      await updateDoc(groupRef, {
        memberUids: arrayUnion(uid),
        [`memberRoles.${uid}`]: "member",
        participantsCount: increment(1),
      });
    } catch {
      throw new Error(
        "Não foi possível entrar na liga — confira se o código está certo, se você já não é membro dela, ou se ela já atingiu o limite de participantes."
      );
    }

    await setDoc(doc(this.groupsCollection(), groupId, "participants", uid), {
      uid,
      nome,
      avatarUrl: auth.currentUser?.photoURL ?? null,
      role: "member" as LeagueRole,
      pontos: 0,
      entrouEm: new Date().toISOString(),
      status: "ativo" as const,
    });

    await addDoc(collection(this.groupsCollection(), groupId, "history"), {
      type: "rule_updated",
      descricao: `${nome} entrou na liga.`,
      administrador: nome,
      createdAt: serverTimestamp(),
    });

    // Só agora dá pra ler — o usuário já é membro, a regra original libera
    // normalmente (isMember passa a ser verdadeiro).
    const fresh = await getDoc(groupRef);
    return this.toDomain(fresh.id, fresh.data() as Record<string, unknown>, uid);
  }

  /**
   * Exclusão de liga (pedido do usuário) — ação destrutiva e irreversível,
   * só admin (garantido pela Firestore Rule também, não só aqui). Apaga
   * o doc da liga e as subcoleções conhecidas ANTES de apagar o pai —
   * Firestore não apaga subcoleções sozinho quando o documento pai é
   * excluído (isso exigiria uma Cloud Function pra ser 100% completo; aqui
   * cobrimos client-side as subcoleções que o app realmente usa). Os
   * `predictions` da coleção raiz que referenciam este leagueId NÃO são
   * limpos (ficam órfãos, mas invisíveis — nunca aparecem em nenhuma
   * consulta depois que a liga some).
   */
  async deleteLeague(leagueId: string): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Você precisa estar autenticado para essa ação.");

    // Exclusão de liga passa pelo backend (Admin SDK), não pelo Firestore
    // direto do navegador — várias subcoleções (history/adjustments são
    // append-only, duels só o participante apaga, etc.) têm regras
    // legitimamente restritivas que nunca permitiriam essa cascata pelo
    // cliente, mesmo pra admin. O endpoint confirma de verdade — pelo
    // registro real em participants/{uid}.role, nunca por nada enviado
    // daqui — que quem está pedindo é admin desta liga específica antes
    // de apagar qualquer coisa. Nenhuma Firestore Rule foi alterada por
    // causa disso; elas continuam protegendo tudo normalmente pra
    // qualquer OUTRA operação.
    let idToken: string;
    try {
      idToken = await currentUser.getIdToken();
    } catch (err) {
      console.error("[FirestoreLeagueRepository] falha ao obter token de autenticação:", err);
      throw err;
    }

    let res: Response;
    try {
      res = await fetch("/api/league/delete-league", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ leagueId }),
      });
    } catch (err) {
      console.error("[FirestoreLeagueRepository] falha de rede ao excluir liga:", err);
      throw new Error("Falha de rede ao tentar excluir a liga. Tente novamente.");
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: null }));
      const message = (body as { error?: string }).error ?? `Falha ao excluir liga: HTTP ${res.status}`;
      throw new Error(message);
    }
  }

  async touchLastAccessed(leagueId: string): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      await setDoc(doc(this.groupsCollection(), leagueId, "participants", uid), { lastAccessedAt: serverTimestamp() }, { merge: true });
    } catch {
      // Nunca deve travar a navegação da liga por causa disso — só perde a
      // ordenação por "acessado recentemente" nessa visita, mais nada.
    }
  }

  /**
   * Sincroniza a foto de perfil nova em TODAS as ligas que o usuário
   * participa. Necessário porque `avatarUrl` é gravado como uma CÓPIA no
   * documento de participante de cada liga (não uma referência ao perfil
   * central) — decisão original pra Ranking/Pódio não precisarem de uma
   * leitura extra por participante. O efeito colateral: sem isto, trocar
   * a foto no Perfil nunca atualiza o que aparece no Ranking/Pódio de
   * nenhuma liga (ficava preso na foto de quando entrou — bug relatado
   * pelo usuário, ex: foto do Google usada no login inicial). Chamado
   * pelo ProfilePage depois de qualquer troca de foto bem-sucedida.
   * Melhor esforço: nunca lança erro, nunca trava a troca de foto em si.
   */
  /**
   * Sincroniza foto e/ou nome de perfil novos em TODAS as ligas que o
   * usuário participa. Necessário porque `avatarUrl`/`nome` são gravados
   * como CÓPIA no documento de participante de cada liga (não uma
   * referência ao perfil central) — decisão original pra Ranking/Pódio/X1
   * não precisarem de uma leitura extra por participante. O efeito
   * colateral: sem isto, trocar a foto OU o nome no Perfil nunca
   * atualiza o que aparece no Ranking/Pódio/X1 de nenhuma liga (ficava
   * preso no valor de quando entrou). Chamado pelo ProfilePage depois de
   * qualquer troca de foto ou nome bem-sucedida — passa só o que mudou.
   * Melhor esforço: nunca lança erro, nunca trava a troca em si.
   */
  async syncProfileAcrossLeagues(fields: { nome?: string; avatarUrl?: string | null }): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (fields.nome === undefined && fields.avatarUrl === undefined) return;
    try {
      const leagues = await this.getMyLeagues();
      if (leagues.length === 0) return;
      const batch = writeBatch(db);
      for (const league of leagues) {
        batch.set(doc(this.groupsCollection(), league.id, "participants", uid), fields, { merge: true });
      }
      await batch.commit();
    } catch (err) {
      console.warn("[FirestoreLeagueRepository] falha ao sincronizar perfil nas ligas (perfil central já foi atualizado normalmente):", err);
    }
  }
}
